/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-16 11:16:17
 **/

export type UserStates = {
	/**
	 * The `users` variable stores a record of user information objects indexed by string keys.
	 * Each user object is a partial object conforming to the structure of `WebServices.User.UserInfo`.
	 *
	 * Key:
	 *   - `string`: A unique identifier for each user.
	 *
	 * Value:
	 *   - `Partial<WebServices.User.UserInfo>`: An object containing a subset of properties defined in the `WebServices.User.UserInfo` interface.
	 */
	users: Record<string, Partial<WebServices.User.UserInfo>>;
	blocked_users: string[]; // for test
};

export type UserCurrentSpace = {
	spaceId: string;
	spaceType: SwaggerModels.UserSpaceType | "CONFERENCE";
	spaceScene?: SwaggerModels.SpaceSceneResponseVO;
};

export interface UserDispatch {
	initialize(): Promise<void>;
	refresh_user(userID: string): Promise<Partial<WebServices.User.UserInfo> | null>;
	me: Partial<WebServices.User.UserInfo>;
	update_me(info: WebServices.User.Me.Update.Request | null): Promise<void>;
	get_user(userID: string): Partial<WebServices.User.UserInfo> | null;
	set_user(userID: string, info: Partial<WebServices.User.UserInfo>): void;
	update_user(updated: PushServices.User.UserUpdated.Data): void;
	is_expired(userID: string): boolean;
	active_status(userID: string): Promise<WebServices.User.UserInfo["activeStatus"]>;
	update_active_status(status: SwaggerModels.UserActiveStatus): Promise<void>;
	check_before_follow(userID: string): Promise<UserCurrentSpace | undefined>;

	set_blocked_users(blocked_users: string[]): void;
	add_blocked_user(userID: string): void;
	remove_blocked_user(userID: string): void;
}

export const InitialUserStates: UserStates = {
	users: {},
	blocked_users: [] // for test
};
